import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ExamsService } from '../exams.service';
import { LoginService } from '../login.service';
import {
  CLINIC_FIELD_TYPES,
  CLINIC_FIELD_WIDTHS,
  cloneDefaultConfig,
  ClinicExamField,
  ClinicExamTemplate,
  ClinicFieldType,
  ClinicFieldTypeMeta,
  fieldTypeMeta,
  normalizeField,
} from '../clinic-exam/clinic-exam.model';

@Component({
  selector: 'app-clinic-exam-builder',
  templateUrl: './clinic-exam-builder.component.html',
  styleUrls: ['./clinic-exam-builder.component.scss'],
  standalone: false
})
export class ClinicExamBuilderComponent implements OnInit {
  palette = CLINIC_FIELD_TYPES;
  widths = CLINIC_FIELD_WIDTHS;

  templates: ClinicExamTemplate[] = [];
  doctors: any[] = [];

  title = '';
  fields: ClinicExamField[] = [];
  removedFields: ClinicExamField[] = [];
  /**
   * Canonical string keys, not raw UIDs. ClinicTemplateAssignments.UID and
   * users.UID do not always come back as the same JS type, and Set.has is a
   * strict comparison — keying on the raw value left every checkbox unticked on
   * edit, and then ticking one added a second, differently-typed entry for the
   * same doctor, which persisted as a duplicate assignment row.
   */
  selectedUIDs = new Set<string>();
  selectedIndex: number = null;

  editingId: number | string = null;
  loading = false;
  showRemoved = false;

  showDeleteFieldConfirm = false;
  fieldToDeleteIndex: number = null;
  showDeleteConfirm = false;
  templateToDelete: ClinicExamTemplate = null;
  lastDeleteWasArchive = false;

  constructor(public es: ExamsService, public ls: LoginService) {}

  ngOnInit() {
    this.load();
    try {
      this.es.getAllPractitioners().subscribe(
        (data: any) => { this.doctors = this.dedupeDoctors(data || []); },
        (err) => { console.log(err); }
      );
    } catch (e) {
      console.log(e);
    }
  }

  load() {
    try {
      this.es.getClinicExamTemplates().subscribe(
        (data: any) => { this.templates = data || []; },
        (err) => { console.log(err); }
      );
    } catch (e) {
      console.log(e);
    }
  }

  // ---------- palette / canvas drag & drop ----------

  dropOnCanvas(event: CdkDragDrop<any>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
      this.selectedIndex = event.currentIndex;
      return;
    }
    const meta: ClinicFieldTypeMeta = event.item.data;
    if (!meta) return;
    const field = this.newField(meta);
    const at = Math.min(event.currentIndex, this.fields.length);
    this.fields.splice(at, 0, field);
    this.selectedIndex = at;
  }

  addFromPalette(meta: ClinicFieldTypeMeta) {
    this.fields.push(this.newField(meta));
    this.selectedIndex = this.fields.length - 1;
  }

  private newField(meta: ClinicFieldTypeMeta): ClinicExamField {
    return {
      FieldID: null,
      FieldType: meta.type,
      Label: meta.defaultLabel,
      Width: meta.type === 'number' ? 'w33' : 'w100',
      Config: cloneDefaultConfig(meta.type),
      autoTexts: [],
    };
  }

  select(i: number) {
    this.selectedIndex = i;
  }
  get selected(): ClinicExamField {
    return this.selectedIndex === null ? null : this.fields[this.selectedIndex];
  }
  meta(type: ClinicFieldType): ClinicFieldTypeMeta {
    return fieldTypeMeta(type);
  }
  typeLabel(type: ClinicFieldType): string {
    return this.meta(type).label;
  }

  moveUp(i: number) {
    if (i <= 0) return;
    moveItemInArray(this.fields, i, i - 1);
    this.selectedIndex = i - 1;
  }
  moveDown(i: number) {
    if (i >= this.fields.length - 1) return;
    moveItemInArray(this.fields, i, i + 1);
    this.selectedIndex = i + 1;
  }

  // ---------- field removal (never destroys answers) ----------

  confirmRemoveField(i: number) {
    this.fieldToDeleteIndex = i;
    this.showDeleteFieldConfirm = true;
  }
  cancelRemoveField() {
    this.fieldToDeleteIndex = null;
    this.showDeleteFieldConfirm = false;
  }
  // The field is only dropped from the submitted list; the server marks it
  // IsActive = 0 and keeps every ClinicExamValues row it already has.
  doRemoveField() {
    if (this.fieldToDeleteIndex === null) return;
    const [removed] = this.fields.splice(this.fieldToDeleteIndex, 1);
    if (removed && removed.FieldID) {
      this.removedFields.push(removed);
    }
    this.selectedIndex = null;
    this.cancelRemoveField();
  }

  restoreField(i: number) {
    const [restored] = this.removedFields.splice(i, 1);
    if (!restored) return;
    this.fields.push(restored);
    this.selectedIndex = this.fields.length - 1;
  }

  // ---------- per-field config editors ----------

  addAutoText(field: ClinicExamField) {
    field.autoTexts.push({ Content: '' });
  }
  removeAutoText(field: ClinicExamField, i: number) {
    field.autoTexts.splice(i, 1);
  }
  autoTextLabel(field: ClinicExamField): string {
    if (field.FieldType === 'picklist') return 'פריטי הרשימה';
    if (field.FieldType === 'yesno') return 'Auto Texts (להערה)';
    return 'Auto Texts';
  }

  /**
   * On a yes/no field the Auto Texts fill the comment box, so with no comment box
   * there is nowhere for them to go and the editor is hidden.
   */
  showsAutoTextEditor(field: ClinicExamField): boolean {
    if (!this.meta(field.FieldType).hasAutoText) return false;
    if (field.FieldType === 'yesno') return !!field.Config.comment;
    return true;
  }

  addOption(field: ClinicExamField) {
    if (!field.Config.options) field.Config.options = [];
    field.Config.options.push('');
  }
  removeOption(field: ClinicExamField, i: number) {
    field.Config.options.splice(i, 1);
  }
  trackByIndex(i: number) {
    return i;
  }

  addTableRow(field: ClinicExamField) {
    if (!field.Config.rows) field.Config.rows = [];
    field.Config.rows.push({ label: '' });
  }
  removeTableRow(field: ClinicExamField, i: number) {
    field.Config.rows.splice(i, 1);
  }

  // ---------- doctors ----------

  private uidKey(uid: any): string {
    return uid === null || uid === undefined ? '' : String(uid);
  }

  toggleDoctor(uid: any) {
    const key = this.uidKey(uid);
    if (this.selectedUIDs.has(key)) this.selectedUIDs.delete(key);
    else this.selectedUIDs.add(key);
  }
  isSelected(uid: any): boolean {
    return this.selectedUIDs.has(this.uidKey(uid));
  }
  /** the userAssociation join has no DISTINCT, so the same doctor can arrive twice */
  private dedupeDoctors(list: any[]): any[] {
    const seen = new Set<string>();
    return list.filter((d) => {
      const key = this.uidKey(d.UID);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  docLabel(uid: any): string {
    const d = this.doctors.find((x) => x.UID == uid);
    return d ? (d.Name || d.EngName || ('UID ' + uid)) : ('UID ' + uid);
  }

  // ---------- persistence ----------

  resetForm() {
    this.editingId = null;
    this.title = '';
    this.fields = [];
    this.removedFields = [];
    this.selectedUIDs = new Set<string>();
    this.selectedIndex = null;
    this.showRemoved = false;
  }

  save() {
    if (!this.title || this.loading) return;
    const body = {
      Title: this.title,
      fields: this.fields.map((f, i) => ({
        FieldID: f.FieldID || null,
        FieldType: f.FieldType,
        Label: f.Label,
        Width: f.Width,
        SortOrder: i,
        Config: f.Config,
        autoTexts: (f.autoTexts || []).filter((a) => a.Content),
      })),
      assignedUIDs: this.doctors.filter((d) => this.isSelected(d.UID)).map((d) => d.UID),
    };
    this.loading = true;
    try {
      const obs = this.editingId
        ? this.es.updateClinicExamTemplate(this.editingId, body)
        : this.es.createClinicExamTemplate(body);
      obs.subscribe(
        (data: any) => { this.templates = data || []; this.resetForm(); this.loading = false; },
        (err) => { console.log(err); this.loading = false; }
      );
    } catch (e) {
      console.log(e);
      this.loading = false;
    }
  }

  edit(t: ClinicExamTemplate) {
    if (this.loading) return;
    this.loading = true;
    try {
      this.es.getClinicExamTemplateDefinition(t.ClinicTemplateID).subscribe(
        (data: any) => {
          this.editingId = t.ClinicTemplateID;
          this.title = data.Title || '';
          this.fields = (data.fields || []).map((f) => this.hydrate(f));
          this.removedFields = (data.inactiveFields || []).map((f) => this.hydrate(f));
          this.selectedUIDs = new Set<string>(
            (data.assignedUIDs || []).map((u) => this.uidKey(u))
          );
          this.selectedIndex = null;
          this.showRemoved = false;
          this.loading = false;
        },
        (err) => { console.log(err); this.loading = false; }
      );
    } catch (e) {
      console.log(e);
      this.loading = false;
    }
  }

  private hydrate(f: any): ClinicExamField {
    return normalizeField(f);
  }

  confirmDelete(t: ClinicExamTemplate) {
    this.templateToDelete = t;
    this.showDeleteConfirm = true;
  }
  cancelDelete() {
    this.templateToDelete = null;
    this.showDeleteConfirm = false;
  }
  doDelete() {
    if (!this.templateToDelete || this.loading) return;
    const id = this.templateToDelete.ClinicTemplateID;
    this.loading = true;
    try {
      this.es.deleteClinicExamTemplate(id).subscribe(
        (data: any) => {
          this.templates = (data && data.templates) || [];
          this.lastDeleteWasArchive = !!(data && data.archived);
          this.showDeleteConfirm = false;
          this.templateToDelete = null;
          this.loading = false;
          if (this.editingId === id) this.resetForm();
        },
        (err) => { console.log(err); this.loading = false; }
      );
    } catch (e) {
      console.log(e);
      this.loading = false;
    }
  }
}
